import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Play, Pause, X, Heart, MoreHorizontal } from 'lucide-react';
import { useRoxboxPlayerContext } from '@/contexts/RoxboxPlayerContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useColorExtraction } from '@/hooks/use-color-extraction';
import { useAuth } from '@/components/AuthProvider';
import { cn } from '@/lib/utils';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrackOptionsSheet } from './TrackOptionsSheet';

export function GlobalMiniPlayer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const { 
    currentTrack, 
    currentAudiobookPage, 
    isPlaying, 
    togglePlay,
    currentTime, 
    duration,
    playingType,
    clearPlayer,
    setNowPlayingOpen
  } = useRoxboxPlayerContext();
  const isMobile = useIsMobile();

  // Check if track is liked
  const { data: isLiked = false } = useQuery({
    queryKey: ['liked-track', currentTrack?.id, user?.id],
    queryFn: async () => {
      if (!user || !currentTrack) return false;
      const { data } = await supabase
        .from('liked_tracks')
        .select('id')
        .eq('user_id', user.id)
        .eq('track_id', currentTrack.id)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !!currentTrack && playingType === 'track',
  });

  // Toggle like mutation
  const toggleLike = useMutation({
    mutationFn: async () => {
      if (!user || !currentTrack) throw new Error('Not authenticated');
      
      if (isLiked) {
        await supabase
          .from('liked_tracks')
          .delete()
          .eq('user_id', user.id)
          .eq('track_id', currentTrack.id);
      } else {
        await supabase
          .from('liked_tracks')
          .insert({ user_id: user.id, track_id: currentTrack.id });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liked-track', currentTrack?.id] });
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] });
      hapticMedium();
    },
  });

  // Dynamic color extraction
  const displayImage = playingType === 'audiobook' 
    ? currentAudiobookPage?.imageUrl 
    : currentTrack?.cover_image_url;
  const { colors } = useColorExtraction(displayImage);

  const hasContent = currentTrack || currentAudiobookPage;
  
  // Hide on Jukebox routes (DesktopPlayerBar/NowPlaying handle playback there)
  const isJukeboxRoute = location.pathname === '/jukebox' || 
                         location.pathname === '/roxbox' ||
                         location.pathname.startsWith('/jukebox/') || 
                         location.pathname.startsWith('/roxbox/');
  
  // Hide on home, my-bundles, profile, and settings pages
  const isHeaderMiniPlayerPage = location.pathname === '/' || 
                                  location.pathname === '/my-bundles' ||
                                  location.pathname === '/profile' ||
                                  location.pathname === '/settings';
  
  // Hide on certain routes
  if (
    isJukeboxRoute ||
    isHeaderMiniPlayerPage ||
    location.pathname.startsWith('/letsbegin') ||
    location.pathname.startsWith('/userflow') ||
    location.pathname.startsWith('/read/')
  ) {
    return null;
  }
  
  if (!hasContent) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  const displayTitle = playingType === 'audiobook'
    ? `Page ${currentAudiobookPage?.pageNumber}`
    : currentTrack?.name;
  const displaySubtitle = playingType === 'audiobook'
    ? currentAudiobookPage?.bookTitle
    : currentTrack?.artist;

  const handleTitleClick = () => {
    hapticLight();
    navigate('/jukebox');
    setNowPlayingOpen(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticLight();
    clearPlayer();
  };

  return (
    <motion.div 
      initial={{ y: isMobile ? -100 : 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: isMobile ? -100 : 100, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={cn(
        "fixed left-0 right-0 z-40 px-4",
        isMobile ? "pt-2" : "bottom-4 pb-2"
      )}
      style={isMobile ? { top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' } : undefined}
    >
      <div className="max-w-2xl mx-auto">
        <div 
          className="relative w-full rounded-xl overflow-hidden backdrop-blur-xl border border-white/10"
          style={{
            background: `linear-gradient(135deg, ${colors.dominant}90 0%, rgba(0,0,0,0.8) 100%)`,
          }}
        >
          {/* Progress bar at top - thinner */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/10">
            <motion.div
              className="h-full bg-white"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          <div className="flex items-center gap-3 p-3 pt-3.5">
            {/* Track image */}
            <button
              onClick={handleTitleClick}
              className="relative w-11 h-11 shrink-0 rounded-lg overflow-hidden active:scale-95 transition-transform"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <img
                src={displayImage}
                alt={displayTitle || 'Now playing'}
                className="w-full h-full object-cover"
              />
              {isPlaying && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="flex items-end gap-[2px] h-3">
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '40%' }} />
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '80%', animationDelay: '0.1s' }} />
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}
            </button>

            {/* Info */}
            <button 
              onClick={handleTitleClick}
              className="flex-1 min-w-0 text-left active:opacity-70 transition-opacity"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <p className="text-white text-sm font-semibold truncate">{displayTitle}</p>
              {displaySubtitle && (
                <p className="text-white/50 text-xs truncate">{displaySubtitle}</p>
              )}
            </button>

            {/* Controls */}
            <div className="flex items-center gap-1" style={{ touchAction: 'manipulation' }}>
              {/* Like button - only for tracks */}
              {playingType === 'track' && currentTrack && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (user) toggleLike.mutate();
                  }}
                  className="w-8 h-8 flex items-center justify-center shrink-0 active:scale-90 transition-transform"
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                >
                  <Heart 
                    className={cn("w-5 h-5", isLiked ? "text-primary fill-primary" : "text-white/50")} 
                  />
                </button>
              )}

              {/* More options */}
              {playingType === 'track' && currentTrack && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    hapticLight();
                    setOptionsOpen(true);
                  }}
                  className="w-8 h-8 flex items-center justify-center shrink-0 active:scale-90 transition-transform text-white/50"
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Track Options Sheet */}
      <TrackOptionsSheet 
        open={optionsOpen} 
        onOpenChange={setOptionsOpen} 
        track={currentTrack || null}
      />
    </motion.div>
  );
}
